/**
 * PATH: packages/email-hub/src/feature/components/email-allocation/AssignmentControl.tsx
 * FILE_ID: EMAILHUB-ALLOCATION-ASSIGNMENT-001-VIEW
 * PURPOSE: Renders the owner chip and teammate picker without mutation logic.
 */
import { useRef } from 'react';
import { UserPlus, X } from 'lucide-react';
import { AdaptiveOverlay } from '@quotech/ui';

import { cn } from '../../../_internal/lib/utils';
import { AllocationAvatar } from './AllocationAvatar';
import type { AssignmentViewModel } from './types';

interface AssignmentControlProps {
  vm: AssignmentViewModel;
  /** Hosts that already render an OWNER cell label suppress the inner one. */
  showLabel?: boolean;
}

export function AssignmentControl({ vm, showLabel = true }: AssignmentControlProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  return (
    <div className="flex items-center gap-2 min-w-0 relative">
      {showLabel && (
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Owner</span>
      )}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => vm.setShowPicker((showing) => !showing)}
        disabled={!!vm.busyKey}
        className={cn(
          'inline-flex min-h-11 max-w-full touch-manipulation items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium sm:min-h-0',
          'hover:bg-muted/60 transition',
          vm.assignment?.assignee_user_id
            ? 'bg-indigo-500/10 border-indigo-500/30 text-foreground'
            : 'bg-background/40 border-dashed border-border text-muted-foreground',
          vm.busyKey?.startsWith('assignee:') ? 'opacity-50' : ''
        )}
        data-testid="email-allocation-assignee-button"
        aria-label={vm.assignment?.assignee_user_id ? 'Change assignee' : 'Pick assignee'}
      >
        {vm.assignment?.assignee_user_id ? (
          <>
            <AllocationAvatar
              size="xs"
              name={vm.assignment.assignee_name || vm.assigneeMember?.name}
              email={vm.assignment.assignee_email || vm.assigneeMember?.email}
              avatar_url={vm.assigneeMember?.avatar_url}
            />
            <span className="truncate max-w-[140px]">
              {vm.assignment.assignee_name ||
                vm.assignment.assignee_email ||
                vm.assigneeMember?.name ||
                'unknown'}
            </span>
          </>
        ) : (
          <>
            <UserPlus className="h-3 w-3" />
            <span>Assign</span>
          </>
        )}
      </button>
      <AdaptiveOverlay
        open={vm.showPicker}
        onOpenChange={vm.setShowPicker}
        anchorRef={triggerRef}
        label="Choose email owner"
        mode="auto"
        popoverWidth={256}
        testId="email-allocation-assignee-popover"
      >
        <div
          className="max-h-64 overflow-y-auto p-1"
          role="listbox"
          aria-label="Available email owners"
        >
          {vm.members.length === 0 && (
            <p className="text-[11px] text-muted-foreground p-2">No teammates available.</p>
          )}
          {vm.assignment?.assignee_user_id && (
            <button
              type="button"
              onClick={() => void vm.pickAssignee(null)}
              className={cn(
                'w-full text-left px-2 py-1.5 rounded text-[12px]',
                'flex items-center gap-2 hover:bg-muted text-rose-600 dark:text-rose-400'
              )}
            >
              <X className="h-3.5 w-3.5" />
              Clear assignee
            </button>
          )}
          {vm.members.map((member) => (
            <button
              key={member.id}
              type="button"
              role="option"
              aria-selected={member.id === vm.assigneeUserId}
              onClick={() => void vm.pickAssignee(member)}
              className={cn(
                'w-full text-left px-2 py-1.5 rounded text-[12px]',
                'flex items-center gap-2 hover:bg-muted',
                member.id === vm.assigneeUserId && 'bg-indigo-500/10'
              )}
            >
              <AllocationAvatar
                size="xs"
                name={member.name}
                email={member.email}
                avatar_url={member.avatar_url}
              />
              <span className="truncate">{member.name}</span>
              {member.email && (
                <span className="ml-auto text-muted-foreground text-[10px] truncate">
                  {member.email}
                </span>
              )}
            </button>
          ))}
        </div>
      </AdaptiveOverlay>
    </div>
  );
}
